const LoginFlowActions = {
	Reset: 'reset',
	ConfirmAccountExists: 'confirmAccountExists',
	Register: 'register',
	SentRegistration: 'sentRegistration',
	ForgotPassword: 'forgotPassword',
	TwoFactorRequired: 'twoFactorRequired',
	AccountLocked: 'accountLocked',
	GuestSetup: 'guestSetup',
	ConvertGuest: 'convertGuest',
	EmailCodeRequired: 'emailCodeRequired',
} as const;

type LoginFlowAction =
	| { type: typeof LoginFlowActions.Reset }
	| { type: typeof LoginFlowActions.ConfirmAccountExists; info: Omit<AccountConfirmedState, 'mode'> }
	| { type: typeof LoginFlowActions.Register }
	| { type: typeof LoginFlowActions.SentRegistration }
	| { type: typeof LoginFlowActions.ForgotPassword; sent?: boolean }
	| { type: typeof LoginFlowActions.TwoFactorRequired; setupRequired?: { code: string } }
	| { type: typeof LoginFlowActions.AccountLocked; lockoutMinutes?: number }
	| { type: typeof LoginFlowActions.GuestSetup }
	| { type: typeof LoginFlowActions.ConvertGuest }
	| { type: typeof LoginFlowActions.EmailCodeRequired; purpose: EmailCodePurpose };

export const LoginFlowActionCreators = {
	reset: (): LoginFlowAction => ({ type: LoginFlowActions.Reset }),
	confirmAccountExists: (info: Omit<AccountConfirmedState, 'mode'>): LoginFlowAction => {
		return { type: LoginFlowActions.ConfirmAccountExists, info };
	},
	register: (): LoginFlowAction => ({ type: LoginFlowActions.Register }),
	sentRegistration: (): LoginFlowAction => ({ type: LoginFlowActions.SentRegistration }),
	forgotPassword: (sent?: boolean): LoginFlowAction => ({ type: LoginFlowActions.ForgotPassword, sent }),
	twoFactorRequired: (setupRequired?: { code: string }): LoginFlowAction => ({
		type: LoginFlowActions.TwoFactorRequired,
		setupRequired,
	}),
	/**
	 * @param lockoutMinutes From the server: minutes left on a timed lockout, 0 for a deactivated account,
	 * undefined when the response did not say.
	 */
	accountLocked: (lockoutMinutes?: number): LoginFlowAction => ({
		type: LoginFlowActions.AccountLocked,
		lockoutMinutes,
	}),
	guestSetup: (): LoginFlowAction => ({ type: LoginFlowActions.GuestSetup }),
	convertGuest: (): LoginFlowAction => ({ type: LoginFlowActions.ConvertGuest }),
	emailCodeRequired: (purpose: EmailCodePurpose): LoginFlowAction => ({
		type: LoginFlowActions.EmailCodeRequired,
		purpose,
	}),
};

interface BaseLoginFlowState {
	loginFlowMode?: LoginFlowMode;
}

interface InitialState extends BaseLoginFlowState {
	mode: 'initial';
}

interface AccountConfirmedState extends BaseLoginFlowState {
	mode: 'emailConfirmed';
	firstName: string | null;
	initials: string;
	isDisabled: boolean;
	resetRequired: boolean;
	/** Minutes left on a failed-attempt lockout; shown above the password so they know to wait or reset. */
	lockoutMinutes?: number;
}

interface RegisterState extends BaseLoginFlowState {
	mode: 'register';
}

interface SentRegistrationState extends BaseLoginFlowState {
	mode: 'sentRegistration';
}

interface ForgotPasswordState extends BaseLoginFlowState {
	mode: 'forgotPassword';
	sent?: boolean;
}

interface TwoFactorRequiredState extends BaseLoginFlowState {
	mode: 'twoFactorRequired';
	setupRequired?: { code: string };
}

interface AccountLockedState extends BaseLoginFlowState {
	mode: 'accountLocked';
	lockoutMinutes?: number;
}

interface GuestSetupState extends BaseLoginFlowState {
	mode: 'guestSetup';
}

interface ConvertGuestState extends BaseLoginFlowState {
	mode: 'convertGuest';
}

/** Which request the emailed code completes: continuing as a returning guest, or turning a guest into an account. */
export type EmailCodePurpose = 'guestLogin' | 'convertGuest';

interface EmailCodeState extends BaseLoginFlowState {
	mode: 'emailCode';
	purpose: EmailCodePurpose;
}

type LoginFlowState =
	| InitialState
	| AccountConfirmedState
	| RegisterState
	| SentRegistrationState
	| ForgotPasswordState
	| TwoFactorRequiredState
	| AccountLockedState
	| GuestSetupState
	| ConvertGuestState
	| EmailCodeState;

export type LoginFlowMode = 'default' | 'registrationOnly' | 'convertGuest';

export const initialLoginFlowState = ({ mode }: { mode?: LoginFlowMode }): LoginFlowState => {
	switch (mode) {
		case 'registrationOnly': {
			return { mode: 'register', loginFlowMode: mode };
		}
		case 'convertGuest': {
			return { mode: 'convertGuest', loginFlowMode: mode };
		}
		default: {
			return { mode: 'initial' };
		}
	}
};

export const loginFlowReducer = (state: LoginFlowState, action: LoginFlowAction): LoginFlowState => {
	switch (action.type) {
		case LoginFlowActions.Reset: {
			const { loginFlowMode } = state;
			switch (loginFlowMode) {
				case 'registrationOnly': {
					return { mode: 'register', loginFlowMode };
				}
				case 'convertGuest': {
					return { mode: 'convertGuest', loginFlowMode };
				}
				default: {
					return { mode: 'initial' };
				}
			}
		}
		case LoginFlowActions.ConfirmAccountExists: {
			const { info } = action;
			return {
				mode: 'emailConfirmed',
				firstName: info.firstName,
				initials: info.initials,
				isDisabled: info.isDisabled,
				resetRequired: info.resetRequired,
				lockoutMinutes: info.lockoutMinutes,
			};
		}
		case LoginFlowActions.Register: {
			const { loginFlowMode } = state;
			return { mode: 'register', loginFlowMode };
		}
		case LoginFlowActions.SentRegistration: {
			const { loginFlowMode } = state;
			return { mode: 'sentRegistration', loginFlowMode };
		}
		case LoginFlowActions.ForgotPassword: {
			const { sent } = action;
			return { mode: 'forgotPassword', sent };
		}
		case LoginFlowActions.TwoFactorRequired: {
			const { setupRequired } = action;
			return { mode: 'twoFactorRequired', setupRequired };
		}
		case LoginFlowActions.AccountLocked: {
			return { mode: 'accountLocked', lockoutMinutes: action.lockoutMinutes };
		}
		case LoginFlowActions.GuestSetup: {
			return { mode: 'guestSetup' };
		}
		case LoginFlowActions.ConvertGuest: {
			return { mode: 'convertGuest' };
		}
		case LoginFlowActions.EmailCodeRequired: {
			const { loginFlowMode } = state;
			return { mode: 'emailCode', purpose: action.purpose, loginFlowMode };
		}
		default:
			throw new Error('Not implemented');
	}
};
